/**
 * ModeratorPanel.jsx
 * Place at: src/components/ModeratorPanel.jsx
 *
 * Slide-in panel for space moderator/co-moderator controls.
 * Tabs: Members | Pending | Mod Log | Settings
 */

import { useState, useEffect, useCallback } from "react";
import {
  fetchSpaceMembers,
  fetchPendingPosts,
  fetchModLog,
  removeSpaceMember,
  muteSpaceMember,
  promoteComoderator,
  revokeComoderator,
  setWeeklyPrompt,
  toggleModeratedMode,
  approveSpacePost,
  removePostFromSpace,
} from "../lib/spaceQueries";
import { formatTimeAgo } from "../lib/utils";

const TABS = ["Members", "Pending", "Mod Log", "Settings"];

const MOD_ACTIONS = {
  remove_post:          "🗑️ Removed post",
  pin_post:             "📌 Pinned post",
  unpin_post:           "📌 Unpinned post",
  remove_member:        "🚫 Removed member",
  mute_member:          "🔇 Muted member",
  approve_post:         "✅ Approved post",
  promote_comoderator:  "⬆️ Promoted co-mod",
  revoke_comoderator:   "⬇️ Revoked co-mod",
  set_prompt:           "💬 Updated weekly prompt",
  toggle_moderated_mode:"🔒 Toggled moderated mode",
};

export default function ModeratorPanel({ space, currentUser, onClose, onSpaceUpdated }) {
  const [activeTab,      setActiveTab]      = useState("Members");
  const [members,        setMembers]        = useState([]);
  const [pendingPosts,   setPendingPosts]   = useState([]);
  const [modLog,         setModLog]         = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);
  const [promptInput,    setPromptInput]    = useState(space?.weekly_prompt || "");
  const [savingPrompt,   setSavingPrompt]   = useState(false);
  const [togglingMode,   setTogglingMode]   = useState(false);
  const [actionLoading,  setActionLoading]  = useState(null); // userId or postId

  const isMainMod = currentUser?.id === space?.moderator_id;

  const loadTab = useCallback(async (tab) => {
    setLoading(true);
    setError(null);
    try {
      if (tab === "Members") {
        const data = await fetchSpaceMembers(space.id);
        setMembers(data);
      } else if (tab === "Pending") {
        const data = await fetchPendingPosts(space.id);
        setPendingPosts(data);
      } else if (tab === "Mod Log") {
        const data = await fetchModLog(space.id);
        setModLog(data);
      }
    } catch (err) {
      setError("Failed to load data.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [space?.id]);

  useEffect(() => {
    if (space?.id) loadTab(activeTab);
  }, [activeTab, space?.id, loadTab]);

  const handleRemoveMember = useCallback(async (userId, username) => {
    if (!window.confirm(`Remove @${username} from this space?`)) return;
    setActionLoading(userId);
    try {
      await removeSpaceMember(space.id, userId, currentUser.id);
      setMembers(prev => prev.filter(m => m.user_id !== userId));
    } catch (err) {
      setError(err.message || "Failed to remove member.");
    } finally {
      setActionLoading(null);
    }
  }, [space?.id]);

  const handleMute = useCallback(async (userId, username, days) => {
    setActionLoading(userId);
    try {
      await muteSpaceMember(space.id, userId, days, currentUser.id);
      setMembers(prev => prev.map(m =>
        m.user_id === userId
          ? { ...m, muted_until: new Date(Date.now() + days * 86400000).toISOString() }
          : m
      ));
    } catch (err) {
      setError(err.message || "Failed to mute member.");
    } finally {
      setActionLoading(null);
    }
  }, [space?.id]);

  const handlePromote = useCallback(async (userId, username) => {
    setActionLoading(userId);
    try {
      await promoteComoderator(space.id, userId, currentUser.id);
      setMembers(prev => prev.map(m =>
        m.user_id === userId ? { ...m, role: "comoderator" } : m
      ));
    } catch (err) {
      setError(err.message || "Failed to promote.");
    } finally {
      setActionLoading(null);
    }
  }, [space?.id]);

  const handleRevoke = useCallback(async (userId) => {
    setActionLoading(userId);
    try {
      await revokeComoderator(space.id, userId, currentUser.id);
      setMembers(prev => prev.map(m =>
        m.user_id === userId ? { ...m, role: "member" } : m
      ));
    } catch (err) {
      setError(err.message || "Failed to revoke.");
    } finally {
      setActionLoading(null);
    }
  }, [space?.id]);

  const handleApprovePost = useCallback(async (postId) => {
    setActionLoading(postId);
    try {
      await approveSpacePost(space.id, postId, currentUser.id);
      setPendingPosts(prev => prev.filter(p => p.id !== postId));
    } catch (err) {
      setError("Failed to approve post.");
    } finally {
      setActionLoading(null);
    }
  }, [space?.id]);

  const handleRejectPost = useCallback(async (postId) => {
    setActionLoading(postId);
    try {
      await removePostFromSpace(space.id, postId, currentUser.id);
      setPendingPosts(prev => prev.filter(p => p.id !== postId));
    } catch (err) {
      setError("Failed to reject post.");
    } finally {
      setActionLoading(null);
    }
  }, [space?.id]);

  const handleSavePrompt = useCallback(async () => {
    setSavingPrompt(true);
    try {
      await setWeeklyPrompt(space.id, promptInput, currentUser.id);
      onSpaceUpdated?.({ weekly_prompt: promptInput });
    } catch (err) {
      setError("Failed to save prompt.");
    } finally {
      setSavingPrompt(false);
    }
  }, [space?.id, promptInput, onSpaceUpdated]);

  const handleToggleMode = useCallback(async () => {
    setTogglingMode(true);
    try {
      const newState = await toggleModeratedMode(space.id, currentUser.id);
      onSpaceUpdated?.({ moderated_mode: newState });
    } catch (err) {
      setError("Failed to toggle moderated mode.");
    } finally {
      setTogglingMode(false);
    }
  }, [space?.id, onSpaceUpdated]);

  return (
    <div style={{
      position:   "fixed",
      inset:      0,
      background: "rgba(0,0,0,0.5)",
      display:    "flex",
      alignItems: "flex-start",
      justifyContent: "flex-end",
      zIndex:     1000,
    }}
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width:      420,
        height:     "100vh",
        background: "var(--card-bg-color)",
        boxShadow:  "-8px 0 32px rgba(0,0,0,0.15)",
        display:    "flex",
        flexDirection: "column",
        overflowY:  "auto",
      }}>

        {/* Header */}
        <div style={{
          padding:      "20px 20px 14px",
          borderBottom: "1px solid var(--border-color)",
          position:     "sticky",
          top:          0,
          background:   "var(--card-bg-color)",
          zIndex:       1,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                🛡️ Moderator Panel
              </div>
              <div style={{ fontWeight: 700, fontSize: "1rem", color: "var(--text-color)" }}>
                {space?.avatar_emoji} {space?.name}
              </div>
            </div>
            <button onClick={onClose} style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 20, color: "var(--text-muted)",
            }}>✕</button>
          </div>

          {/* Health badge */}
          {space?.health_label && (
            <div style={{
              display:     "inline-flex",
              alignItems:  "center",
              gap:         4,
              marginTop:   8,
              padding:     "3px 10px",
              borderRadius: 20,
              fontSize:    "0.72rem",
              fontWeight:  700,
              background:  space.health_label === "thriving" ? "#22c55e20" :
                           space.health_label === "active"   ? "#3b82f620" :
                           space.health_label === "quiet"    ? "#f9731620" : "#6366f120",
              color:       space.health_label === "thriving" ? "#16a34a" :
                           space.health_label === "active"   ? "#2563eb" :
                           space.health_label === "quiet"    ? "#ea580c" : "#6366f1",
            }}>
              {space.health_label === "thriving" ? "🟢" :
               space.health_label === "active"   ? "🔵" :
               space.health_label === "quiet"    ? "🟡" : "⚪"} Space is {space.health_label}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{
          display:      "flex",
          borderBottom: "1px solid var(--border-color)",
          padding:      "0 16px",
          position:     "sticky",
          top:          80,
          background:   "var(--card-bg-color)",
          zIndex:       1,
        }}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding:      "10px 12px",
                border:       "none",
                background:   "none",
                borderBottom: activeTab === tab ? "2px solid var(--primary-color)" : "2px solid transparent",
                color:        activeTab === tab ? "var(--primary-color)" : "var(--text-muted)",
                fontWeight:   activeTab === tab ? 700 : 500,
                fontSize:     "0.82rem",
                cursor:       "pointer",
                fontFamily:   "inherit",
                transition:   "all 0.15s",
              }}
            >
              {tab}
              {tab === "Pending" && pendingPosts.length > 0 && (
                <span style={{
                  marginLeft:   5,
                  background:   "#ef4444",
                  color:        "#fff",
                  borderRadius: "50%",
                  fontSize:     10,
                  fontWeight:   700,
                  padding:      "1px 5px",
                }}>
                  {pendingPosts.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            margin:       "12px 16px 0",
            padding:      "8px 12px",
            borderRadius: 8,
            background:   "rgba(239,68,68,0.1)",
            border:       "1px solid rgba(239,68,68,0.3)",
            color:        "#ef4444",
            fontSize:     "0.82rem",
            display:      "flex",
            justifyContent: "space-between",
          }}>
            {error}
            <button onClick={() => setError(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444" }}>✕</button>
          </div>
        )}

        {/* Content */}
        <div style={{ padding: "16px", flex: 1 }}>

          {loading ? (
            <div style={{ textAlign: "center", color: "var(--text-muted)", padding: 32 }}>Loading…</div>
          ) : (

            /* ── MEMBERS TAB ─────────────────────────────── */
            activeTab === "Members" ? (
              <div>
                <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginBottom: 12 }}>
                  {members.length} active members
                </div>
                {members.length === 0 ? (
                  <div style={{ textAlign: "center", color: "var(--text-muted)", padding: 24 }}>No members yet</div>
                ) : members.map(m => {
                  const username = m.feed_users?.username || m.user_id;
                  const isSelf   = m.user_id === currentUser?.id;
                  const isMod    = m.role === "moderator";
                  const isComod  = m.role === "comoderator";
                  const isMuted  = m.muted_until && new Date(m.muted_until) > new Date();

                  return (
                    <div key={m.user_id} style={{
                      display:      "flex",
                      alignItems:   "center",
                      gap:          10,
                      padding:      "10px 0",
                      borderBottom: "1px solid var(--border-subtle, #f3f4f6)",
                    }}>
                      <div style={{
                        width:        34,
                        height:       34,
                        borderRadius: "50%",
                        background:   "var(--primary-light)",
                        color:        "var(--primary-color)",
                        display:      "flex",
                        alignItems:   "center",
                        justifyContent: "center",
                        fontWeight:   700,
                        fontSize:     "0.85rem",
                        flexShrink:   0,
                      }}>
                        {username[0]?.toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-color)" }}>
                          @{username}
                          {isMod   && <span style={badgeStyle("#6366f1")}> 🛡️ Mod</span>}
                          {isComod && <span style={badgeStyle("#8b5cf6")}> 🛡️ Co-mod</span>}
                          {isMuted && <span style={badgeStyle("#f97316")}> 🔇 Muted</span>}
                        </div>
                        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                          {m.feed_users?.follower_count || 0} followers · joined {formatTimeAgo(m.joined_at)}
                        </div>
                      </div>

                      {/* Actions — don't show on self or main mod */}
                      {!isSelf && !isMod && (
                        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                          {/* Mute */}
                          <select
                            disabled={actionLoading === m.user_id}
                            defaultValue=""
                            onChange={e => {
                              if (e.target.value) handleMute(m.user_id, username, parseInt(e.target.value));
                              e.target.value = "";
                            }}
                            style={{
                              padding:      "3px 6px",
                              borderRadius: 6,
                              border:       "1.5px solid var(--border-color)",
                              background:   "var(--card-bg-color)",
                              color:        "var(--text-muted)",
                              fontSize:     "0.72rem",
                              cursor:       "pointer",
                              fontFamily:   "inherit",
                            }}
                          >
                            <option value="" disabled>🔇 Mute</option>
                            <option value="1">1 day</option>
                            <option value="3">3 days</option>
                            <option value="7">7 days</option>
                          </select>

                          {/* Promote/Revoke — main mod only */}
                          {isMainMod && (
                            isComod ? (
                              <button onClick={() => handleRevoke(m.user_id)}
                                disabled={actionLoading === m.user_id}
                                style={smallBtnStyle("var(--text-muted)")}>
                                Revoke
                              </button>
                            ) : (
                              <button onClick={() => handlePromote(m.user_id, username)}
                                disabled={actionLoading === m.user_id}
                                style={smallBtnStyle("#8b5cf6")}>
                                Promote
                              </button>
                            )
                          )}

                          {/* Remove */}
                          <button
                            onClick={() => handleRemoveMember(m.user_id, username)}
                            disabled={actionLoading === m.user_id}
                            style={smallBtnStyle("#ef4444")}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            /* ── PENDING TAB ─────────────────────────────── */
            ) : activeTab === "Pending" ? (
              <div>
                {pendingPosts.length === 0 ? (
                  <div style={{ textAlign: "center", color: "var(--text-muted)", padding: 32 }}>
                    <div style={{ fontSize: "2rem", marginBottom: 8 }}>✅</div>
                    No posts awaiting approval
                  </div>
                ) : pendingPosts.map(post => (
                  <div key={post.id} style={{
                    padding:      "12px",
                    borderRadius: 10,
                    border:       "1.5px solid var(--border-color)",
                    marginBottom: 10,
                    background:   "var(--bg-secondary)",
                  }}>
                    <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginBottom: 6 }}>
                      @{post.author_username} · {formatTimeAgo(post.created_at)}
                    </div>
                    <div style={{ fontSize: "0.875rem", color: "var(--text-color)", lineHeight: 1.5, marginBottom: 10 }}>
                      {post.content}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => handleApprovePost(post.id)}
                        disabled={actionLoading === post.id}
                        style={{
                          padding:      "6px 14px",
                          borderRadius: 8,
                          border:       "none",
                          background:   "#22c55e",
                          color:        "#fff",
                          fontSize:     "0.78rem",
                          fontWeight:   600,
                          cursor:       "pointer",
                          fontFamily:   "inherit",
                        }}
                      >
                        ✅ Approve
                      </button>
                      <button
                        onClick={() => handleRejectPost(post.id)}
                        disabled={actionLoading === post.id}
                        style={{
                          padding:      "6px 14px",
                          borderRadius: 8,
                          border:       "1.5px solid #ef4444",
                          background:   "transparent",
                          color:        "#ef4444",
                          fontSize:     "0.78rem",
                          fontWeight:   600,
                          cursor:       "pointer",
                          fontFamily:   "inherit",
                        }}
                      >
                        🗑️ Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>

            /* ── MOD LOG TAB ──────────────────────────────── */
            ) : activeTab === "Mod Log" ? (
              <div>
                {modLog.length === 0 ? (
                  <div style={{ textAlign: "center", color: "var(--text-muted)", padding: 32 }}>
                    No actions yet
                  </div>
                ) : modLog.map(entry => (
                  <div key={entry.id} style={{
                    padding:      "10px 0",
                    borderBottom: "1px solid var(--border-subtle, #f3f4f6)",
                    display:      "flex",
                    gap:          10,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.82rem", color: "var(--text-color)" }}>
                        {MOD_ACTIONS[entry.action] || entry.action}
                        {entry.detail && (
                          <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> — {entry.detail}</span>
                        )}
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 2 }}>
                        by @{entry.actor_username} · {formatTimeAgo(entry.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            /* ── SETTINGS TAB ────────────────────────────── */
            ) : activeTab === "Settings" ? (
              <div>
                {/* Weekly prompt */}
                <div style={{ marginBottom: 24 }}>
                  <div style={sectionTitleStyle}>💬 Weekly Theme Prompt</div>
                  <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: "4px 0 10px" }}>
                    Shown on your space card in the main feed. Encourages members to post.
                  </p>
                  <textarea
                    value={promptInput}
                    onChange={e => setPromptInput(e.target.value)}
                    placeholder="e.g. What helped you cope this week?"
                    rows={2}
                    maxLength={150}
                    style={{
                      width:        "100%",
                      padding:      "10px 12px",
                      borderRadius: 8,
                      border:       "1.5px solid var(--border-color)",
                      background:   "var(--bg-secondary)",
                      color:        "var(--text-color)",
                      fontSize:     "0.875rem",
                      fontFamily:   "inherit",
                      resize:       "none",
                      outline:      "none",
                      boxSizing:    "border-box",
                      marginBottom: 8,
                    }}
                  />
                  <button
                    onClick={handleSavePrompt}
                    disabled={savingPrompt}
                    style={{
                      padding:      "8px 18px",
                      borderRadius: 8,
                      border:       "none",
                      background:   "var(--primary-color)",
                      color:        "#fff",
                      fontSize:     "0.82rem",
                      fontWeight:   600,
                      cursor:       savingPrompt ? "not-allowed" : "pointer",
                      opacity:      savingPrompt ? 0.7 : 1,
                      fontFamily:   "inherit",
                    }}
                  >
                    {savingPrompt ? "Saving…" : "Save Prompt"}
                  </button>
                </div>

                {/* Moderated mode */}
                <div style={{
                  padding:      "16px",
                  borderRadius: 12,
                  border:       "1.5px solid var(--border-color)",
                  background:   "var(--bg-secondary)",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={sectionTitleStyle}>🔒 Moderated Mode</div>
                      <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: "4px 0 0" }}>
                        New posts require your approval before appearing.
                      </p>
                    </div>
                    <button
                      onClick={handleToggleMode}
                      disabled={togglingMode}
                      style={{
                        padding:      "6px 14px",
                        borderRadius: 8,
                        border:       "none",
                        background:   space?.moderated_mode ? "#ef4444" : "#22c55e",
                        color:        "#fff",
                        fontSize:     "0.78rem",
                        fontWeight:   600,
                        cursor:       togglingMode ? "not-allowed" : "pointer",
                        opacity:      togglingMode ? 0.7 : 1,
                        fontFamily:   "inherit",
                        flexShrink:   0,
                        marginLeft:   12,
                      }}
                    >
                      {togglingMode ? "…" : space?.moderated_mode ? "Turn Off" : "Turn On"}
                    </button>
                  </div>
                  <div style={{
                    marginTop:  10,
                    fontSize:   "0.78rem",
                    fontWeight: 700,
                    color:      space?.moderated_mode ? "#22c55e" : "var(--text-muted)",
                  }}>
                    {space?.moderated_mode ? "🟢 Currently ON" : "⚪ Currently OFF"}
                  </div>
                </div>
              </div>
            ) : null
          )}
        </div>
      </div>
    </div>
  );
}

const badgeStyle = (color) => ({
  marginLeft:   5,
  padding:      "1px 6px",
  borderRadius: 10,
  background:   `${color}18`,
  color,
  fontSize:     "0.68rem",
  fontWeight:   700,
});

const smallBtnStyle = (color) => ({
  padding:      "3px 8px",
  borderRadius: 6,
  border:       `1.5px solid ${color}50`,
  background:   "transparent",
  color,
  fontSize:     "0.72rem",
  fontWeight:   600,
  cursor:       "pointer",
  fontFamily:   "inherit",
});

const sectionTitleStyle = {
  fontSize:    "0.82rem",
  fontWeight:  700,
  color:       "var(--text-color)",
  marginBottom: 0,
};